import React, { useState } from 'react';
import { MenuIcon, XIcon, ChevronDownIcon } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
import { useNavigate, Link } from 'react-router-dom';
export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const {
    t,
    locale,
    direction
  } = useLanguage();
  const navigate = useNavigate();
  const aboutPath = locale === 'ar' ? '/ar/about' : '/about';
  const switchLanguage = (lang: 'en' | 'ar') => {
    if (lang === 'ar') {
      navigate('/ar');
    } else {
      navigate('/');
    }
    setIsMenuOpen(false);
  };
  const groupMenuItems = [{
    label: t('header.about'),
    href: aboutPath
  }, {
    label: t('header.corporateGovernance'),
    href: '#'
  }, {
    label: t('header.advisorsAuditors'),
    href: '#'
  }];
  const businessMenuItems = [{
    label: t('header.cardIssuing'),
    href: '#'
  }, {
    label: t('header.atmAcquiring'),
    href: '#'
  }, {
    label: t('header.merchantAcquiring'),
    href: '#'
  }, {
    label: t('header.mobilePayment'),
    href: '#'
  }, {
    label: t('header.consultancy'),
    href: '#'
  }, {
    label: t('header.otherServices'),
    href: '#'
  }];
  return <header className="absolute top-0 left-0 w-full z-50 px-6 py-5 md:px-16 lg:px-24 flex items-center bg-transparent text-white" dir={direction} data-id="element-109">
      {/* Logo - far left (or right in RTL) */}
      <div className="flex items-center" data-id="element-110">
        <Link to={locale === 'ar' ? '/ar' : '/'} data-id="element-111">
          <img src="https://cdn.magicpatterns.com/uploads/pGgoddoE2KXfnRu1yQ5keh/logo.svg" alt="CSC Group" className="h-10 w-auto" data-id="element-112" />
        </Link>
      </div>

      {/* Spacer to push everything to the other side */}
      <div className="flex-1" data-id="element-113" />

      {/* Desktop: Nav + Language + Icons all grouped */}
      <div className="hidden lg:flex items-center gap-8" data-id="element-114">
        {/* Navigation */}
        <nav className="flex items-center gap-10 text-sm font-semibold tracking-wide" data-id="element-115">
          {/* The Group - with dropdown */}
          <div className="relative" onMouseEnter={() => setActiveDropdown('group')} onMouseLeave={() => setActiveDropdown(null)} data-id="element-116">
            <a href="#" className="hover:text-[#25b8d5] transition-colors py-2 block" data-id="element-117">
              {t('header.theGroup')}
            </a>

            {activeDropdown === 'group' && <>
                <span className="absolute left-0 w-full h-[3px] bg-[#25B8D6] z-10" data-id="element-118" />
                <div className={`absolute top-full pt-[3px] ${direction === 'rtl' ? 'right-0' : 'left-0'}`} data-id="element-119">
                  <div className="bg-white min-w-[280px] shadow-xl overflow-hidden" style={{
                borderRadius: direction === 'rtl' ? '0 0 0 50px' : '0 0 50px 0'
              }} data-id="element-120">
                    <div className="py-0" data-id="element-121">
                      {groupMenuItems.map((item, index) => <a key={index} href={item.href} className="block text-[#061d76] font-semibold text-base py-3 px-8 hover:bg-[#25B8D6] hover:text-white transition-colors text-start" data-id="element-122">
                          {item.label}
                        </a>)}
                    </div>
                  </div>
                </div>
              </>}
          </div>

          {/* Businesses - with dropdown */}
          <div className="relative" onMouseEnter={() => setActiveDropdown('businesses')} onMouseLeave={() => setActiveDropdown(null)} data-id="element-123">
            <a href="#" className="hover:text-[#25b8d5] transition-colors py-2 block" data-id="element-124">
              {t('header.businesses')}
            </a>

            {activeDropdown === 'businesses' && <>
                <span className="absolute left-0 w-full h-[3px] bg-[#25B8D6] z-10" data-id="element-125" />
                <div className={`absolute top-full pt-[3px] ${direction === 'rtl' ? 'right-0' : 'left-0'}`} data-id="element-126">
                  <div className="bg-white min-w-[280px] shadow-xl overflow-hidden" style={{
                borderRadius: direction === 'rtl' ? '0 0 0 50px' : '0 0 50px 0'
              }} data-id="element-127">
                    <div className="py-0" data-id="element-128">
                      {businessMenuItems.map((item, index) => <a key={index} href={item.href} className="block text-[#061d76] font-semibold text-base py-3 px-8 hover:bg-[#25B8D6] hover:text-white transition-colors text-start" data-id="element-129">
                          {item.label}
                        </a>)}
                      <a href="#" className="block text-[#061d76] font-semibold text-base py-3 px-8 hover:bg-[#25B8D6] hover:text-white transition-colors text-start" data-id="element-130">
                        {t('header.ourTools')}
                      </a>
                    </div>
                  </div>
                </div>
              </>}
          </div>

          <a href="#" className="hover:text-[#25b8d5] transition-colors" data-id="element-131">
            {t('header.partners')}
          </a>
          <a href="#" className="hover:text-[#25b8d5] transition-colors" data-id="element-132">
            {t('header.contact')}
          </a>
        </nav>

        {/* Divider */}
        <div className="w-px h-6 bg-white/20" data-id="element-133" />

        {/* Language Selectors */}
        <div className="flex items-center gap-2 text-sm font-medium" data-id="element-134">
          <button onClick={() => switchLanguage('en')} className={`${locale === 'en' ? 'text-[#25b8d5]' : 'text-white/60 hover:text-[#25b8d5]'} transition-colors`} data-id="element-135">
            En
          </button>
          <span className="text-white/40" data-id="element-136">|</span>
          <button onClick={() => switchLanguage('ar')} className={`${locale === 'ar' ? 'text-[#25b8d5]' : 'text-white/60 hover:text-[#25b8d5]'} transition-colors`} data-id="element-137">
            AR
          </button>
        </div>

        {/* Icon Buttons */}
        <div className="flex items-center gap-3" data-id="element-138">
          <a href="#" className="w-10 h-10 rounded-full bg-[#25b8d5] flex items-center justify-center hover:bg-white hover:scale-110 transition-all duration-300 group" aria-label="Home" data-id="element-139">
            <img src="https://cdn.magicpatterns.com/uploads/mJm2cCyBBxRzfedF4ndUce/homeicon.svg" alt="" className="w-5 h-5 group-hover:brightness-0 transition-all" data-id="element-140" />
          </a>
          <a href="#" className="w-10 h-10 rounded-full bg-[#25b8d5] flex items-center justify-center hover:bg-white hover:scale-110 transition-all duration-300 group" aria-label="Card services" data-id="element-141">
            <img src="https://cdn.magicpatterns.com/uploads/rr3bWdmyzs8eihXcWN94ix/card-icon.svg" alt="" className="w-5 h-5 group-hover:brightness-0 transition-all" data-id="element-142" />
          </a>
        </div>
      </div>

      {/* Mobile Menu Button - only show when menu is closed */}
      {!isMenuOpen && <button className="lg:hidden p-2" onClick={() => setIsMenuOpen(true)} aria-label="Open menu" data-id="element-143">
          <MenuIcon className="w-6 h-6" data-id="element-144" />
        </button>}

      {/* Mobile Menu - Full Screen */}
      {isMenuOpen && <div className="fixed inset-0 bg-[#061d76] z-50 flex flex-col lg:hidden mobile-menu-enter" dir={direction} data-id="element-145">
          {/* Header area with logo and close */}
          <div className="flex items-center justify-between px-6 py-5 mobile-menu-item" style={{
        animationDelay: '150ms'
      }} data-id="element-146">
            <img src="https://cdn.magicpatterns.com/uploads/pGgoddoE2KXfnRu1yQ5keh/logo.svg" alt="CSC Group" className="h-10 w-auto" data-id="element-147" />
            <button className="p-2 text-white" onClick={() => setIsMenuOpen(false)} aria-label="Close menu" data-id="element-148">
              <XIcon className="w-6 h-6" data-id="element-149" />
            </button>
          </div>

          {/* Nav links - centered vertically */}
          <div className="flex-1 flex flex-col justify-center px-10 gap-2 overflow-y-auto" data-id="element-150">
            {/* The Group - expandable */}
            <div className="mobile-menu-item" style={{
          animationDelay: '200ms'
        }} data-id="element-151">
              <button className="text-2xl font-bold text-white hover:text-[#25b8d5] transition-colors w-full text-start py-3 flex items-center justify-between" onClick={() => setMobileExpanded(mobileExpanded === 'group' ? null : 'group')} data-id="element-152">
                {t('header.theGroup')}
                <ChevronDownIcon className={`w-5 h-5 transition-transform duration-300 ${mobileExpanded === 'group' ? 'rotate-180' : ''}`} data-id="element-153" />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${mobileExpanded === 'group' ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-154">
                <div className="px-4 pb-4 flex flex-col gap-3" data-id="element-155">
                  {groupMenuItems.map((item, index) => <a key={index} href={item.href} className="text-base text-white hover:text-[#25b8d5] transition-colors text-start" onClick={() => setIsMenuOpen(false)} data-id="element-156">
                      {item.label}
                    </a>)}
                </div>
              </div>
            </div>

            {/* Businesses - expandable */}
            <div className="mobile-menu-item" style={{
          animationDelay: '300ms'
        }} data-id="element-157">
              <button className="text-2xl font-bold text-white hover:text-[#25b8d5] transition-colors w-full text-start py-3 flex items-center justify-between" onClick={() => setMobileExpanded(mobileExpanded === 'businesses' ? null : 'businesses')} data-id="element-158">
                {t('header.businesses')}
                <ChevronDownIcon className={`w-5 h-5 transition-transform duration-300 ${mobileExpanded === 'businesses' ? 'rotate-180' : ''}`} data-id="element-159" />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${mobileExpanded === 'businesses' ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-160">
                <div className="px-4 pb-4 flex flex-col gap-3" data-id="element-161">
                  {businessMenuItems.map((item, index) => <a key={index} href={item.href} className="text-base text-white hover:text-[#25b8d5] transition-colors text-start" onClick={() => setIsMenuOpen(false)} data-id="element-162">
                      {item.label}
                    </a>)}
                  <a href="#" className="text-base text-white hover:text-[#25b8d5] transition-colors text-start" onClick={() => setIsMenuOpen(false)} data-id="element-163">
                    {t('header.ourTools')}
                  </a>
                </div>
              </div>
            </div>

            <a href="#" className="text-2xl font-bold text-white hover:text-[#25b8d5] transition-colors py-3 mobile-menu-item text-start" style={{
          animationDelay: '400ms'
        }} onClick={() => setIsMenuOpen(false)} data-id="element-164">
              {t('header.partners')}
            </a>
            <a href="#" className="text-2xl font-bold text-white hover:text-[#25b8d5] transition-colors py-3 mobile-menu-item text-start" style={{
          animationDelay: '500ms'
        }} onClick={() => setIsMenuOpen(false)} data-id="element-165">
              {t('header.contact')}
            </a>
          </div>

          {/* Bottom area - language + icons */}
          <div className="px-10 pb-10 flex flex-col gap-6 mobile-menu-item" style={{
        animationDelay: '600ms'
      }} data-id="element-166">
            <div className="w-full h-px bg-white/10" data-id="element-167" />
            <div className="flex items-center justify-between" data-id="element-168">
              <div className="flex items-center gap-3 text-sm font-medium" data-id="element-169">
                <button onClick={() => switchLanguage('en')} className={`${locale === 'en' ? 'text-[#25b8d5]' : 'text-white hover:text-[#25b8d5]'} transition-colors`} data-id="element-170">
                  En
                </button>
                <span className="text-white/40" data-id="element-171">|</span>
                <button onClick={() => switchLanguage('ar')} className={`${locale === 'ar' ? 'text-[#25b8d5]' : 'text-white hover:text-[#25b8d5]'} transition-colors`} data-id="element-172">
                  AR
                </button>
              </div>
              <div className="flex items-center gap-3" data-id="element-173">
                <a href="#" className="w-10 h-10 rounded-full bg-[#25b8d5] flex items-center justify-center" data-id="element-174">
                  <img src="https://cdn.magicpatterns.com/uploads/mJm2cCyBBxRzfedF4ndUce/homeicon.svg" alt="" className="w-5 h-5" data-id="element-175" />
                </a>
                <a href="#" className="w-10 h-10 rounded-full bg-[#25b8d5] flex items-center justify-center" data-id="element-176">
                  <img src="https://cdn.magicpatterns.com/uploads/rr3bWdmyzs8eihXcWN94ix/card-icon.svg" alt="" className="w-5 h-5" data-id="element-177" />
                </a>
              </div>
            </div>
          </div>
        </div>}
    </header>;
}